import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);
  // iter70 · Capability set for the current user. Empty set until /me/capabilities resolves.
  const [capabilities, setCapabilities] = useState(new Set());
  const [plan, setPlan] = useState(null);

  const loadCapabilities = useCallback(async () => {
    try {
      const { data } = await api.get("/me/capabilities");
      setCapabilities(new Set(data.capabilities || []));
      setPlan(data.plan || null);
    } catch {
      setCapabilities(new Set());
      setPlan(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      loadCapabilities();
    } catch {
      setUser(false);
      setCapabilities(new Set());
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [loadCapabilities]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    loadCapabilities();
    return data.user;
  }, [loadCapabilities]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    loadCapabilities();
    return data.user;
  }, [loadCapabilities]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed (continuing to clear local session):", err?.message || err);
    }
    setUser(false);
    setCapabilities(new Set());
    setPlan(null);
  }, []);

  const value = useMemo(
    () => ({
      user, loading, login, register, logout,
      refresh: checkAuth, setUser, formatApiErrorDetail,
      // iter70 capability surface
      capabilities, plan, refreshCapabilities: loadCapabilities,
    }),
    [user, loading, login, register, logout, checkAuth,
     capabilities, plan, loadCapabilities],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * iter70 · useCapability(capName) — returns `true` when the current user
 * has the given capability. Cheap, memoisable, and stays reactive when
 * the user's plan or overrides change.
 */
export function useCapability(capName) {
  const ctx = useContext(AuthContext);
  if (!ctx) return false;
  return ctx.capabilities?.has?.(capName) || false;
}
